import { useTranslation } from "@opendash/i18n";
import { ScheduleDataObjectType } from "@opendash/schedule";
import { Button, Form, Input, InputNumber, Select, Tabs } from "antd";
import dayjs, { Dayjs } from "dayjs";
import objectSupport from "dayjs/plugin/objectSupport";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { EndDatePicker } from "./EndDatePicker";
import { StartDatePicker } from "./StartDatePicker";

require("dayjs/locale/de");
dayjs.locale("de");
dayjs.extend(objectSupport);

const TIMES = ["days", "weeks", "months", "years"] as const;

type Props = {
  onSubmit?: (values: ScheduleDataObjectType<Dayjs>) => Promise<void> | void;
  initialValues?: ScheduleDataObjectType<Date>;
};

/**
 * React component to create cron strings visually
 * @author Nico Vitt
 */
export const CronEditor: React.FC<Props> = observer((props: Props) => {
  const t = useTranslation();
  const [form] = Form.useForm<ScheduleDataObjectType<Dayjs>>();
  const values = Form.useWatch<ScheduleDataObjectType<Dayjs>>([], form);
  const scheduleType =
    (values as ScheduleDataObjectType<Dayjs>)?.schedule_type || "timestamp";

  // Outcommented code underneath is for cron support, wich was implemented in the past but is not used anymore.
  // If we want to support cron again, we can uncomment it and use the CronInput component.
  // Also make sure to update the ScheduleDataObjectType to include cron support.
  const unitOptions = useMemo(
    () =>
      TIMES.map((value) => ({
        label: t(`opendash:timestamp.${value}`),
        value,
      })),
    [t]
  );

  const initialValues: ScheduleDataObjectType<Dayjs> = useMemo(() => {
    if (props.initialValues) {
      return {
        ...props.initialValues,
        timestamp: {
          ...props.initialValues.timestamp,
          startDate: dayjs(props.initialValues.timestamp.startDate),
          endDate: props.initialValues.timestamp.endDate
            ? dayjs(props.initialValues.timestamp.endDate)
            : undefined,
        },
      };
    }
    return {
      schedule_type: "timestamp",
      timestamp: {
        startDate: dayjs(),
        endDate: undefined,
        number: 1,
        unit: "days",
      },
    };
  }, [props.initialValues]);
  // useEffect(() => {
  //   if (scheduleType === "timestamp") form.setFieldValue("cron", undefined);
  //   else if (scheduleType === "cron")
  //     form.setFieldValue("timestamp", undefined);
  // }, [scheduleType]);

  return (
    <Form<ScheduleDataObjectType<Dayjs>>
      layout="vertical"
      form={form}
      onFinish={props.onSubmit}
      initialValues={initialValues}
    >
      <Form.Item<ScheduleDataObjectType>
        name="schedule_type"
        hidden
        initialValue="timestamp"
      >
        <Input />
      </Form.Item>

      <Tabs
        onChange={(key) => form.setFieldValue("schedule_type", key)}
        items={[
          {
            key: "timestamp",
            label: t("opendash:timestamp.label"),
            children: (
              <>
                <Form.Item<ScheduleDataObjectType>
                  label={t("maintenance:schedule.create.form.startdate.label")}
                  name={["timestamp", "startDate"]}
                  rules={[
                    {
                      required: scheduleType === "timestamp",
                      message: t(
                        "maintenance:schedule.create.form.startdate.rules.required"
                      ),
                    },
                  ]}
                >
                  <StartDatePicker setOnlyWeeks={null} />
                </Form.Item>
                <Form.Item<ScheduleDataObjectType>
                  label={t("maintenance:schedule.create.form.enddate.label")}
                  name={["timestamp", "endDate"]}
                  rules={[{ required: false }]}
                >
                  <EndDatePicker disabled={false} />
                </Form.Item>

                <Form.Item
                  name={["timestamp", "number"]}
                  label={t("opendash:timestamp.number")}
                  rules={[{ required: scheduleType === "timestamp" }]}
                  style={{ flex: 1 }}
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={["timestamp", "unit"]}
                  label={t("opendash:timestamp.unit")}
                  rules={[{ required: scheduleType === "timestamp" }]}
                  style={{ flex: 1 }}
                >
                  <Select options={unitOptions} />
                </Form.Item>
                {/* {unitFactor === TIMES.months && (
                  <Alert
                    type="info"
                    message={t("opendash:timestamp.warning.months")}
                  />
                )}
                {unitFactor === TIMES.years && (
                  <Alert
                    type="info"
                    message={t("opendash:timestamp.warning.years")}
                  />
                )} */}
              </>
            ),
          },
          // {
          //   key: "cron",
          //   label: t("opendash:cron.label"),
          //   children: (
          //     <Form.Item<ScheduleDataObjectType> name="cron">
          //       <CronInput />
          //     </Form.Item>
          //   ),
          // },
        ]}
      />
      <Button type="primary" htmlType="submit">
        {t("opendash:cron.submit")}
      </Button>
    </Form>
  );
});
